import { Collection, CollectionRuleResponse, RuleOperator } from "@authory-packages/core";
import { useFormik } from "formik";
import { useState } from "react";
import { getKeywordRules, getOperatorLabel, getSourceRules, getTypeRules } from "../../utils/collectionRulesParser";
import { generateCollectionRules } from "../../utils/generateCollectionRules";
import { V3Button } from "../Button";
import { DropdownOptionCTA } from "../DropdownOptionCTA";
import { ModalLoadingSpinner } from "../ReusableStyledComponents/ModalLoadingSpinner";
import { RightAlignPanel } from "../ReusableStyledComponents/RightAlignPanel";
import { V3BodyGrey48 } from "../ReusableStyledComponents/V3BodyGrey48";
import { SidebarCustomizedSelect } from "../SidebarCustomizedSelect";
import { V3TextInput } from "../TextInput/V3TextInput";
import { VerticalSpacing } from "../VerticalSpacing";
import { ConditionSizr } from "./V3EditCollectionRules.styles";
import { FakeSelectWithModal } from "../FakeSelect";
import { SIPWrapper } from "../SourceInProgress/SourceInProgress.styles";
import { V3CaptionGrey35 } from "../ReusableStyledComponents/V3CaptionGrey35";
import { RuleFakeSelectorOperatorWrapper } from "../FakeSelect/FakeSelect.styles";
import { V3ButtonColor } from "../Button/V3Button.types";
import { NotificationInlineLink } from "../ReusableStyledComponents";
import { APPLICATION_ROUTES } from "../../shared-logic";
import { InlineLink } from "../ReusableStyledComponents/InlineLink";

export interface RulesFormik {
    source: string[];
    text: string;
    type: string[];
    operator: RuleOperator;
}

interface V3EditCollectionRulesProps {
    onCloseHandler: () => void;
    rules: CollectionRuleResponse[];
    srcList: { label: string; value: string }[];
    collection: Collection;
    onRulesUpdate: (operator: RuleOperator, rules: CollectionRuleResponse[]) => Promise<Collection | undefined>;
    hasRuleConflict?: boolean;
}

export const V3EditCollectionRules = ({
    onCloseHandler,
    srcList,
    rules,
    collection,
    hasRuleConflict = false,
    onRulesUpdate
}: V3EditCollectionRulesProps) => {
    const keywordRules = getKeywordRules(rules);
    const sourceRules = getSourceRules(rules, srcList);
    const typeRules = getTypeRules(rules);

    const [rulesSubmitting, setRuleSubmitting] = useState(false);

    const formik = useFormik<RulesFormik>({
        enableReinitialize: true,
        initialValues: {
            text: keywordRules.length ? keywordRules.join(", ") : "",
            source: sourceRules,
            type: typeRules,
            operator: collection.operator
        },
        onSubmit: async (values) => {
            const computedRules = generateCollectionRules(values.source, values.type, values.text);

            try {
                setRuleSubmitting(true);
                await onRulesUpdate(values.operator, computedRules);
                onCloseHandler();
            } catch {
                setRuleSubmitting(false);
            }
        }
    });

    const ConditionComponent = () => (
        <ConditionSizr>
            <SidebarCustomizedSelect
                optionTitle={getOperatorLabel(formik.values.operator)}
                mobileTitle="Pick a condition"
                renderPopperElement={(_active, setShowPopper) => (
                    <>
                        <DropdownOptionCTA
                            onClick={() => {
                                formik.setFieldValue("operator", RuleOperator.All);
                                setShowPopper(false);
                            }}
                            label={getOperatorLabel(RuleOperator.All)}
                            active={formik.values.operator === RuleOperator.All}
                        />{" "}
                        <DropdownOptionCTA
                            onClick={() => {
                                formik.setFieldValue("operator", RuleOperator.Any);
                                setShowPopper(false);
                            }}
                            label={getOperatorLabel(RuleOperator.Any)}
                            active={formik.values.operator === RuleOperator.Any}
                        />
                    </>
                )}
            />
        </ConditionSizr>
    );

    return (
        <>
            <VerticalSpacing bottom={12}>
                <V3BodyGrey48>Authory can automatically update this collection with future content. You can set the conditions below.</V3BodyGrey48>
            </VerticalSpacing>
            {hasRuleConflict && (
                <VerticalSpacing bottom={14}>
                    <SIPWrapper $customPadding="12px" $allowUserThemable={false}>
                        <div>
                            <V3CaptionGrey35>
                                We couldn't automatically add a new auto-update rule because you already have different auto-update rules in place for
                                this collection. Please double check and update them as you please.{" "}
                                <InlineLink
                                    href="https://authory.com/resources/tutorials/why-you-might-see-a-message-about-auto-update-rules"
                                    target="_blank"
                                >
                                    Learn more
                                </InlineLink>
                            </V3CaptionGrey35>
                        </div>
                    </SIPWrapper>
                </VerticalSpacing>
            )}
            <VerticalSpacing bottom={24}>
                <V3BodyGrey48>Add future content items automatically if they match these conditions:</V3BodyGrey48>
            </VerticalSpacing>
            <RuleFakeSelectorOperatorWrapper>
                <div>
                    <FakeSelectWithModal
                        label="Is from source:"
                        placeholder="Please select"
                        items={srcList}
                        selectedItems={formik.values.source}
                        onModalSubmitClose={(selectedModalItems) => {
                            formik.setFieldValue("source", selectedModalItems);
                        }}
                        tooltipContent={
                            !srcList.length ? (
                                <>
                                    Please{" "}
                                    <NotificationInlineLink href={`${APPLICATION_ROUTES.CONTENT}?target=addContent`}>
                                        add work samples
                                    </NotificationInlineLink>{" "}
                                    to your account to be able to select any sources.
                                </>
                            ) : undefined
                        }
                    />
                </div>
                <div>
                    <ConditionComponent />
                </div>
            </RuleFakeSelectorOperatorWrapper>
            <RuleFakeSelectorOperatorWrapper>
                <div>
                    <VerticalSpacing bottom={12}>
                        <V3TextInput
                            label="Contains the following keywords:"
                            name="text"
                            placeholder="e.g. Apple, Microsoft"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.text}
                        />
                    </VerticalSpacing>
                </div>
                <div>
                    <ConditionComponent />
                </div>
            </RuleFakeSelectorOperatorWrapper>
            <RuleFakeSelectorOperatorWrapper>
                <div>
                    <FakeSelectWithModal
                        label="Is content type:"
                        placeholder="Please select"
                        items={[]}
                        useTypesList
                        selectedItems={formik.values.type}
                        onModalSubmitClose={(selectedModalItems) => {
                            formik.setFieldValue("type", selectedModalItems);
                        }}
                    />
                </div>
                <div>
                    <ConditionSizr />
                </div>
            </RuleFakeSelectorOperatorWrapper>
            <VerticalSpacing top={32}>
                <RightAlignPanel>
                    {!!onCloseHandler && (
                        <div>
                            <V3Button text="Close" color={V3ButtonColor.secondary} onClick={onCloseHandler} />
                        </div>
                    )}
                    <div>
                        {rulesSubmitting ? (
                            <ModalLoadingSpinner />
                        ) : (
                            <V3Button
                                text="Save"
                                autoWidth
                                onClick={() => {
                                    formik.submitForm();
                                }}
                            />
                        )}
                    </div>
                </RightAlignPanel>
            </VerticalSpacing>
        </>
    );
};
